import { LINE_ROLES, RATE_TYPES, ROLE, SINGLE_USE_ROLES } from './mappingRoles';
import type { ColumnAssignment, ColumnMappingConfig, MappingSuggestion, MappingTarget, Structure, TabularColumn } from './types';

/**
 * The editable state of the "Match your columns" form, kept free of React so the rules are easy to test. Nothing in
 * here talks to the server; the server stays the authority and re-checks every mapping it receives.
 */

export type RowOrigin = 'suggestion' | 'saved' | 'confirmed' | 'person';

export interface RowState {
  /** '' means the person has not chosen yet (such a column is sent as "Do not import"). */
  role: string;
  /** Hours only: the header of the earning column the hours belong to. */
  earningLink: string;
  label: string;
  jurisdictionLevel: string;
  stateCode: string;
  rateType: string;
  taxable: boolean;
  origin: RowOrigin;
  /** EPN's reason, kept while the row still shows EPN's suggestion. */
  reason: string;
}

const BLANK: RowState = {
  role: '', earningLink: '', label: '', jurisdictionLevel: 'Other', stateCode: '', rateType: 'Regular', taxable: true, origin: 'person', reason: '',
};

function fromSuggestion(s: MappingSuggestion): RowState {
  return {
    ...BLANK,
    role: s.role,
    jurisdictionLevel: s.jurisdictionLevel ?? 'Other',
    stateCode: s.stateCode ?? '',
    rateType: s.rateType ?? 'Regular',
    taxable: s.taxable ?? true,
    origin: 'suggestion',
    reason: `${s.reason} (${s.confidence.toLowerCase()} confidence)`,
  };
}

function fromAssignment(a: ColumnAssignment, origin: RowOrigin): RowState {
  return {
    ...BLANK,
    role: a.role,
    earningLink: a.role === ROLE.Hours ? (a.code ?? '') : '',
    label: a.label ?? '',
    jurisdictionLevel: a.jurisdictionLevel ?? 'Other',
    stateCode: a.stateCode ?? '',
    rateType: a.rateType ?? 'Regular',
    taxable: a.taxable ?? true,
    origin,
  };
}

const sameHeader = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/** A confirmed mapping (if the person already made one) wins; otherwise EPN's suggestions, shown as suggestions. */
export function initialRows(structure: Structure, columns: TabularColumn[]): RowState[] {
  const confirmed = structure.currentMapping;
  return columns.map((column) => {
    const assignment = confirmed?.columns.find((a) => sameHeader(a.sourceColumn, column.header));
    if (assignment) return fromAssignment(assignment, 'confirmed');
    if (confirmed) return { ...BLANK };
    const suggestion = structure.suggestions.find((s) => sameHeader(s.sourceColumn, column.header));
    return suggestion ? fromSuggestion(suggestion) : { ...BLANK };
  });
}

/** Fills the form from a saved mapping. It is still only a form: the person has to confirm it. */
export function rowsFromProfile(mapping: ColumnMappingConfig, columns: TabularColumn[]): RowState[] {
  return columns.map((column) => {
    const assignment = mapping.columns.find((a) => sameHeader(a.sourceColumn, column.header));
    return assignment ? fromAssignment(assignment, 'saved') : { ...BLANK };
  });
}

/** Changing a column's meaning starts it from clean defaults (keeping a label the person typed). */
export function withRole(previous: RowState, role: string, suggestion?: MappingSuggestion): RowState {
  const base = suggestion && suggestion.role === role ? fromSuggestion(suggestion) : { ...BLANK, role };
  return { ...base, role, label: previous.label, origin: 'person', reason: '' };
}

export interface RequiredItem {
  key: string;
  label: string;
  done: boolean;
}

/** The things EPN cannot import without, in plain words, with whether each one has been chosen. */
export function requiredItems(rows: RowState[]): RequiredItem[] {
  const has = (role: string) => rows.some((r) => r.role === role);
  return [
    { key: 'employee', label: "Employee ID (the employee's ID in your old system, never a name)", done: has(ROLE.EmployeeId) },
    { key: 'payDate', label: 'Pay date', done: has(ROLE.PayDate) },
    { key: 'periodStart', label: 'Pay period start', done: has(ROLE.PeriodStart) },
    { key: 'periodEnd', label: 'Pay period end (never the pay date)', done: has(ROLE.PeriodEnd) },
    { key: 'name', label: 'Employee name (a full name, or first name and last name)', done: has(ROLE.FullName) || (has(ROLE.FirstName) && has(ROLE.LastName)) },
    { key: 'earning', label: 'At least one earning (money the employee earned)', done: has(ROLE.Earning) },
  ];
}

const STATE_CODE = /^[A-Za-z]{2}$/;

/** Why the Confirm button is off, as short sentences. Empty means the mapping can be confirmed. */
export function blockers(rows: RowState[], columns: TabularColumn[], labelOf: (role: string) => string = (r) => r): string[] {
  const out: string[] = [];
  const missing = requiredItems(rows).filter((i) => !i.done);
  if (missing.length > 0) out.push(`Still needed: ${missing.map((m) => m.label.replace(/ \(.*\)$/, '')).join(', ')}.`);

  const unclassified = columns.filter((_, i) => rows[i]!.role === ROLE.DeductionNeedsClassification);
  if (unclassified.length > 0) {
    out.push(`Choose whether each deduction is taken before tax or after tax: ${unclassified.map((c) => c.header).join(', ')}.`);
  }

  for (const role of SINGLE_USE_ROLES) {
    const used = columns.filter((_, i) => rows[i]!.role === role);
    if (used.length > 1) out.push(`More than one column is set to "${labelOf(role)}" (${used.map((c) => c.header).join(', ')}). Only one column can have this meaning.`);
  }

  columns.forEach((column, i) => {
    const row = rows[i]!;
    if (row.role === ROLE.Hours && !row.earningLink) out.push(`Choose which earning the hours in "${column.header}" belong to.`);
    if ((row.role === ROLE.EmployeeTax || row.role === ROLE.EmployerTax) && row.jurisdictionLevel === 'State' && !STATE_CODE.test(row.stateCode)) {
      out.push(`Enter a two-letter state code for "${column.header}".`);
    }
  });
  return out;
}

/** The mapping to send. A column the person never chose is sent as "Do not import this column". */
export function toAssignments(rows: RowState[], columns: TabularColumn[]): ColumnAssignment[] {
  return columns.map((column, i) => {
    const row = rows[i]!;
    const role = row.role === '' ? ROLE.Ignore : row.role;
    const a: ColumnAssignment = { sourceColumn: column.header, role };
    if (LINE_ROLES.has(role) && row.label.trim() !== '') a.label = row.label.trim();
    if (role === ROLE.Earning) {
      a.rateType = (RATE_TYPES as readonly string[]).includes(row.rateType) ? row.rateType : 'Regular';
      a.taxable = row.taxable;
    }
    if (role === ROLE.EmployeeTax || role === ROLE.EmployerTax) {
      a.jurisdictionLevel = row.jurisdictionLevel;
      if (row.jurisdictionLevel === 'State') a.stateCode = row.stateCode.toUpperCase();
    }
    if (role === ROLE.Hours) a.code = row.earningLink;
    return a;
  });
}

/** Groups the server's choices for the dropdown, in the order the server listed them. */
export function groupTargets(targets: MappingTarget[]): { group: string; targets: MappingTarget[] }[] {
  const groups: { group: string; targets: MappingTarget[] }[] = [];
  for (const t of targets) {
    const existing = groups.find((g) => g.group === t.group);
    if (existing) existing.targets.push(t);
    else groups.push({ group: t.group, targets: [t] });
  }
  return groups;
}
